import {
    boolean,
    intersection,
    literal,
    nativeEnum,
    object,
    record,
    string,
    TypeOf,
    union,
} from 'zod';

/* -------------------------------- Alignment ------------------------------- */

export enum Alignment {
    Left = 'left',
    Center = 'center',
    Right = 'right',
}

/* ----------------------------- Classification ----------------------------- */

export enum Classification {
    Collection = 'collection',
    Product = 'product',
}

/* ------------------------------- CornerStyle ------------------------------ */

export enum CornerStyle {
    Rounded = 'rounded',
    Squared = 'squared',
}

/* ------------------------------- ElementType ------------------------------ */

export enum ElementType {
    AToZ = 'a-to-z',
    Carousel = 'carousel',
    Grid = 'grid',
    Swimlane = 'swimlane',
    VideoCarousel = 'video-carousel',
    Text = 'text',
    QrCode = 'qr-code',
    Banner = 'banner',
}

/* -------------------------------- LabelSize ------------------------------- */

export enum LabelSize {
    ExtraSmall = 'extra-small',
    Small = 'small',
    Medium = 'medium',
    Large = 'large',
    ExtraLarge = 'extra-large',
}

/* --------------------------------- Margin --------------------------------- */

export enum Margin {
    None = 'none',
    Small = 'small',
    Medium = 'medium',
    Large = 'large',
}

/* ------------------------------ PageTemplate ------------------------------ */

export enum PageTemplate {
    Flex = 'flex',
    Info = 'info',
}

/* -------------------------------- Position -------------------------------- */

export enum Position {
    TopLeft = 'top-left',
    TopRight = 'top-right',
}

/* ---------------------------------- Ratio --------------------------------- */

export enum Ratio {
    '16:9' = '16:9',
    '16:7' = '16:7',
    '21:9' = '21:9',
    '5:4' = '5:4',
    '4:3' = '4:3',
    '2:3' = '2:3',
    '1:1' = '1:1',
}

/* -------------------------------- TileSize -------------------------------- */

export enum TileSize {
    Small = 'small',
    Medium = 'medium',
    Large = 'large',
}

/* -------------------------------- LinkType -------------------------------- */

export enum LinkType {
    Internal = 'internal',
    External = 'external',
    System = 'system',
}

/* ------------------------------- SystemPage ------------------------------- */

export enum SystemPage {
    AccountDetails = 'account-details',
    AppDetails = 'app-details',
    ForgotPassword = 'forgot-password',
    ForgotPasswordCheckEmail = 'forgot-password-check-email',
    ForgotPasswordReset = 'forgot-password-reset',
    Login = 'login',
    Logout = 'logout',
    OpenSourceLicenses = 'open-source-licenses',
    Pair = 'pair',
    Pay = 'pay',
    Payments = 'payments',
    PrivacySettings = 'privacy-settings',
    PurchaseCode = 'purchase-code',
    Registration = 'registration',
    RegistrationCompleted = 'registration-completed',
    RegistrationEmailVerified = 'registration-email-verified',
    RegistrationEmailVerify = 'registration-email-verify',
    Search = 'search',
    Subscribe = 'subscribe',
    Subscriptions = 'subscriptions',
    UpdateCard = 'update-card',
}

/* ----------------------------- LinkDisplayType ---------------------------- */

export enum LinkDisplayType {
    Text = 'text',
    Image = 'image',
    TextAndImage = 'text-and-image',
}
/* ----------------------------- LinkBackground ----------------------------- */

export enum LinkBackground {
    Feature = 'feature',
    None = 'none',
}
/* -------------------------------- BaseLink -------------------------------- */

export const BaseLink = object({
    id: string(),
    type: nativeEnum(LinkType),
    name: string(),
    display: nativeEnum(LinkDisplayType),
    background: nativeEnum(LinkBackground),
    openNewTab: boolean(),
    lightImageId: string().optional(),
    darkImageId: string().optional(),
    url: string().optional(),
    itemId: string().optional(),
    systemPage: nativeEnum(SystemPage).optional(),
});
export type BaseLink = TypeOf<typeof BaseLink>;

/* ------------------------------- SystemLink ------------------------------- */

export const SystemLink = BaseLink.extend({
    type: literal(LinkType.System),
    systemPage: nativeEnum(SystemPage),
});
export type SystemLink = TypeOf<typeof SystemLink>;

/* ------------------------------ InternalLink ------------------------------ */

export const InternalLink = BaseLink.extend({
    type: literal(LinkType.Internal),
    itemId: string(),
});
export type InternalLink = TypeOf<typeof InternalLink>;

/* ------------------------------ ExternalLink ------------------------------ */

export const ExternalLink = BaseLink.extend({
    type: literal(LinkType.External),
    url: string(),
});
export type ExternalLink = TypeOf<typeof ExternalLink>;

/* ---------------------------------- Link ---------------------------------- */

export const Link = union([InternalLink, ExternalLink, SystemLink]);
export type Link = TypeOf<typeof Link>;

/* ------------------------------ ImageSection ------------------------------ */

export const ModelImageSection = intersection(
    object({
        master: string().optional(),
    }),
    record(string(), string().optional()),
);
export type ModelImageSection = TypeOf<typeof ModelImageSection>;

/* ------------------------------- ModelImage ------------------------------- */

export const ModelImage = object({
    master: string(),
    thumb: ModelImageSection.optional(),
    carousel: ModelImageSection.optional(),
    hero: ModelImageSection.optional(),
});
export type ModelImage = TypeOf<typeof ModelImage>;
